import { useEffect, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Bell, BellOff, CalendarCheck, Clock, Settings, Sparkles, type LucideIcon } from "lucide-react";
import { panelFrameStyle, panelInnerStyle } from "./card-frame";
import { FLOAT_FRAME_SHADOW, popOrigin, useMenu, usePresence, type MenuGroup } from "./use-presence";
import { BEVEL_CHIP_ON, BEVEL_NAVY_FACE } from "@majourneys/shared/ui";

const NAVY = "#110E39";
const GOLD = "#E5B265";
const GOLD_DARK = "#8A6425";
const GREEN = "#1A7A44";
const MUTED = "#5a5d77";
const HAIRLINE = "rgba(17,14,57,0.09)";
const SOFT_TINT = "rgba(17,14,57,0.055)";
/** An unread row wears a wash of the brand gold, not an alarm colour. */
const UNREAD_WASH = "rgba(229,178,101,0.08)";
const PANEL_RADIUS = 18;
const PANEL_INNER_RADIUS = 14;

type NotificationType = "booking" | "promo" | "system" | "reminder";

type Notification = {
  readonly id: string;
  readonly type: NotificationType;
  readonly title: string;
  readonly body: string;
  readonly href?: string;
  readonly createdAt: number;
  read: boolean;
};

const STORAGE_KEY = "majourneys:notifications:v2";

/** Booking is green, promo gold, system navy, reminder muted: no red or amber in the set. */
const TYPE_META: Record<NotificationType, { icon: LucideIcon; color: string }> = {
  booking: { icon: CalendarCheck, color: GREEN },
  promo: { icon: Sparkles, color: GOLD_DARK },
  system: { icon: Settings, color: NAVY },
  reminder: { icon: Clock, color: MUTED },
};

function loadNotifications(): Notification[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw) as Notification[];
  } catch {
    // ignore
  }
  return [];
}

function saveNotifications(items: Notification[]) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {
    // ignore
  }
}

function relativeTime(ts: number, t: (key: string, opts?: Record<string, unknown>) => string): string {
  const diff = Date.now() - ts;
  const min = Math.floor(diff / 60000);
  if (min < 1) return t("common.just_now");
  if (min < 60) return t("common.minutes_ago", { count: min });
  const hr = Math.floor(min / 60);
  if (hr < 24) return t("common.hours_ago", { count: hr });
  const day = Math.floor(hr / 24);
  if (day < 7) return t("common.days_ago", { count: day });
  return t("common.weeks_ago", { count: Math.floor(day / 7) });
}

export function NotificationsDropdown({ className, group }: { readonly className?: string; readonly group?: MenuGroup }) {
  const { t, i18n } = useTranslation();
  const menu = useMenu(group, "bell");
  const presence = usePresence(menu.open);
  const navigate = useNavigate();
  const [items, setItems] = useState<Notification[]>(() => loadNotifications());

  useEffect(() => {
    saveNotifications(items);
  }, [items]);

  const unread = items.filter((n) => !n.read).length;

  function markRead(id: string) {
    setItems((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
  }

  function markAllRead() {
    setItems((prev) => prev.map((n) => ({ ...n, read: true })));
  }

  function clearAll() {
    setItems([]);
  }

  return (
    <div ref={menu.ref} className={["relative shrink-0", className].filter(Boolean).join(" ")}>
      <button
        type="button"
        onClick={menu.toggle}
        aria-haspopup="menu"
        aria-expanded={menu.open}
        aria-label={t("common.notifications", { defaultValue: "Notifications" })}
        className="mj-press relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full"
        style={menu.open ? { background: BEVEL_NAVY_FACE, color: "#fff", boxShadow: BEVEL_CHIP_ON } : { background: SOFT_TINT, color: NAVY }}
      >
        <Bell size={18} strokeWidth={2.2} aria-hidden />
        {unread > 0 && (
          <span
            aria-hidden="true"
            className="absolute -top-0.5 -end-0.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-1 text-[10px] font-bold leading-none tabular-nums"
            style={{ background: GOLD, color: NAVY, boxShadow: "0 0 0 2px #fff" }}
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
        {unread > 0 && <span className="sr-only">{t("common.unread_count", { count: unread })}</span>}
      </button>

      {presence.mounted ? (
        <div
          ref={presence.ref}
          role="menu"
          aria-label={t("common.notifications", { defaultValue: "Notifications" })}
          data-state={presence.state}
          data-instant={menu.instant ? "true" : undefined}
          className={`mj-pop absolute end-0 top-[calc(100%+8px)] z-[var(--z-dropdown)] w-[380px] ${
            presence.state === "closed" ? "pointer-events-none" : ""
          }`}
          style={{ ...panelFrameStyle(PANEL_RADIUS), boxShadow: FLOAT_FRAME_SHADOW, transformOrigin: popOrigin("end", i18n.dir()) }}
        >
          {/* Two elements: the list scrolls, so the inner card has to clip. */}
          <div className="overflow-hidden" style={panelInnerStyle(PANEL_RADIUS)}>
            <div
              className="flex items-center gap-2 px-4 py-3"
              style={{ background: SOFT_TINT, boxShadow: `inset 0 -1px 0 ${HAIRLINE}`, borderRadius: `${PANEL_INNER_RADIUS}px ${PANEL_INNER_RADIUS}px 0 0` }}
            >
              <span className="text-[14px] font-bold" style={{ color: NAVY }}>
                {t("common.notifications", { defaultValue: "Notifications" })}
              </span>
              {unread > 0 && (
                <span className="text-[11px] tabular-nums" style={{ color: MUTED }}>
                  {unread} {t("common.unread", { defaultValue: "unread" })}
                </span>
              )}
              {unread > 0 && (
                <button
                  type="button"
                  onClick={markAllRead}
                  className="mj-press ms-auto min-h-6 rounded-full px-1.5 text-[11.5px] font-bold hover:bg-black/5"
                  style={{ color: GOLD_DARK }}
                >
                  {t("common.mark_all_read", { defaultValue: "Mark all read" })}
                </button>
              )}
            </div>

            {items.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-1 px-6 py-12 text-center">
                <span className="mb-2 grid h-12 w-12 place-items-center rounded-full" style={{ background: SOFT_TINT }}>
                  <BellOff size={22} strokeWidth={2} style={{ color: MUTED }} aria-hidden />
                </span>
                <span className="text-[13.5px] font-bold" style={{ color: NAVY }}>
                  {t("common.no_notifications", { defaultValue: "You're all caught up" })}
                </span>
                <span className="text-[12px]" style={{ color: MUTED }}>
                  {t("common.no_notifications_hint", { defaultValue: "We'll ping you here when something needs your attention." })}
                </span>
              </div>
            ) : (
              <ul className="max-h-[420px] overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                {items.map((n, i) => {
                  const { icon: TypeIcon, color } = TYPE_META[n.type];
                  const inner = (
                    <>
                      <span
                        aria-hidden="true"
                        className="grid h-9 w-9 shrink-0 place-items-center rounded-full"
                        style={{ background: SOFT_TINT }}
                      >
                        <TypeIcon size={16} strokeWidth={2.2} style={{ color }} />
                      </span>
                      <span className="flex min-w-0 flex-1 flex-col gap-0.5 leading-tight">
                        <span className="flex items-center gap-1.5">
                          <span className="min-w-0 flex-1 break-words text-[13px] font-bold" style={{ color: NAVY }}>{n.title}</span>
                          {!n.read && (
                            <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: GOLD }} aria-label={t("common.unread")} />
                          )}
                        </span>
                        <span className="line-clamp-2 text-[12px] leading-snug" style={{ color: MUTED }}>{n.body}</span>
                        <span className="mt-0.5 text-[10.5px] font-semibold uppercase" style={{ color: MUTED, letterSpacing: "0.06em" }}>
                          {relativeTime(n.createdAt, t)}
                        </span>
                      </span>
                    </>
                  );
                  const handleClick = (e?: React.MouseEvent) => {
                    markRead(n.id);
                    menu.close();
                    if (n.href) {
                      e?.preventDefault();
                      navigate({ href: n.href });
                    }
                  };
                  const itemCls = "flex w-full items-start gap-3 px-4 py-3 text-start hover:bg-black/5";
                  const itemStyle = { borderTop: i === 0 ? "none" : `1px solid ${HAIRLINE}`, background: n.read ? undefined : UNREAD_WASH };
                  return (
                    <li key={n.id}>
                      {n.href ? (
                        <a href={n.href} role="menuitem" onClick={handleClick} className={itemCls} style={itemStyle}>
                          {inner}
                        </a>
                      ) : (
                        <button type="button" role="menuitem" onClick={() => handleClick()} className={itemCls} style={itemStyle}>
                          {inner}
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}

            {items.length > 0 && (
              <div
                className="flex items-center justify-between px-4 py-2.5"
                style={{ background: SOFT_TINT, boxShadow: `inset 0 1px 0 ${HAIRLINE}`, borderRadius: `0 0 ${PANEL_INNER_RADIUS}px ${PANEL_INNER_RADIUS}px` }}
              >
                <button
                  type="button"
                  onClick={clearAll}
                  className="mj-press min-h-6 rounded-full px-1.5 text-[11.5px] font-bold hover:bg-black/5"
                  style={{ color: MUTED }}
                >
                  {t("common.clear_all", { defaultValue: "Clear all" })}
                </button>
                <a
                  href="/account"
                  onClick={(e) => { e.preventDefault(); menu.close(); navigate({ href: "/account" }); }}
                  className="mj-press inline-flex min-h-6 items-center rounded-full px-1.5 text-[11.5px] font-bold hover:bg-black/5"
                  style={{ color: NAVY }}
                >
                  {t("common.notification_settings", { defaultValue: "Settings" })}
                </a>
              </div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
